// Events: proposers days, industry days and the like, one set at a time, picked on the left. Your events: those held
// by the organizations you follow, or in one of your feeds. Browse: every event, or one organization's (followed or
// not), with a Follow button.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router";
import { api, qs } from "../api/client";
import type { EventHost, EventsPage } from "../api/types";
import { useUI } from "../app/context";
import { GlobalSearch } from "../components/GlobalSearch";
import { EventRow } from "../components/Rows";

export function Events() {
  const [params, setParams] = useSearchParams();
  const show = params.get("show") ?? ""; // unset: the server picks "following" for someone who follows anyone, else "all"
  const kind = params.get("kind") ?? "";
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  useEffect(() => {
    const t = window.setTimeout(() => setQuery(q.trim()), 150);
    return () => window.clearTimeout(t);
  }, [q]);
  const data = useQuery({
    queryKey: ["events", "page", show, kind, query],
    queryFn: () => api<EventsPage>(`/api/events${qs({ show, kind, q: query })}`),
    placeholderData: (prev) => prev,
  });
  const pick = (value: string) => setParams(value ? { show: value } : {}, { replace: true }); // a new set drops the kind
  const setKind = (value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set("kind", value);
    else next.delete("kind");
    setParams(next, { replace: true });
  };
  const d = data.data;
  return (
    <>
      <div className="top">
        <div className="date">Events</div>
        <GlobalSearch />
      </div>
      <h1>Events</h1>
      <p className="lede">Proposers days, industry days and other events coming up, from the organizations holding them.</p>
      {!d ? (
        <p className="empty">{data.isError ? "Couldn’t load events. Try again." : "Loading…"}</p>
      ) : (
        <div className="events-wrap">
          <div className="events-layout">
            <Nav key={d.show} d={d} onPick={pick} />
            <div>
              <Header d={d} onPick={pick} />
              <div className="tools events-tools">
                <select className="kind-pick" aria-label="Kind of event" value={kind} onChange={(e) => setKind(e.target.value)}>
                  <option value="">All kinds</option>
                  {d.kinds.map((k) => (
                    <option key={k.kind} value={k.kind}>
                      {k.label} ({k.count})
                    </option>
                  ))}
                </select>
                <input className="filter" id="events-q" type="search" placeholder="Filter these events" aria-label="Filter these events" value={q} onChange={(e) => setQ(e.target.value)} />
              </div>
              <div className="rows" id="rows">
                <Rows d={d} filtered={!!(kind || query)} />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/** The sets to pick from: yours (the organizations you follow, each of your feeds), then every event and each
 *  organization. On a narrow page, one menu. */
function Nav({ d, onPick }: { d: EventsPage; onPick: (show: string) => void }) {
  const tree = useTree(d.hosts);
  const selectedOrg = d.show.startsWith("org:") ? d.show.slice(4) : "";
  const [open, setOpen] = useState<Set<string>>(() => new Set(tree.chain(selectedOrg)));
  const item = (value: string, label: ReactNode, count: number | null, extra = "") => (
    <button key={value} className={`nav-item ${extra}`.trim()} aria-current={d.show === value} onClick={() => onPick(value)}>
      <span className="fl">{label}</span>
      {count !== null ? <span className="fc">{count || ""}</span> : null}
    </button>
  );
  const renderHost = (h: EventHost, depth: number): ReactNode => {
    const kids = tree.children(h.id);
    const expanded = open.has(h.id);
    const n = tree.total(h.id);
    return (
      <li key={h.id}>
        <div className="host-row" style={{ paddingLeft: depth * 14 }}>
          {kids.length ? (
            <button
              className="caret"
              aria-label={expanded ? `Hide those under ${h.label}` : `Show those under ${h.label}`}
              aria-expanded={expanded}
              onClick={() => setOpen((s) => new Set(expanded ? [...s].filter((x) => x !== h.id) : [...s, h.id]))}
            >
              {expanded ? "▾" : "▸"}
            </button>
          ) : (
            <span className="caret" />
          )}
          {item(
            `org:${h.id}`,
            <>
              {h.label}
              {h.followed ? <span className="dot-followed" title="You follow it"> ●</span> : null}
            </>,
            n,
            n ? "" : "zero",
          )}
        </div>
        {kids.length && expanded ? <ul>{kids.map((c) => renderHost(c, depth + 1))}</ul> : null}
      </li>
    );
  };
  return (
    <>
      <nav className="events-nav" aria-label="Which events">
        <h3>Your events</h3>
        {item("following", "Organizations you follow", d.sets.following)}
        {d.feeds.map((f) => item(`feed:${f.id}`, f.name, f.count, "feed"))}
        <h3>Browse</h3>
        {item("all", "All events", d.sets.all)}
        <ul className="host-tree">{tree.roots.map((h) => renderHost(h, 0))}</ul>
      </nav>
      <select className="events-nav-menu" aria-label="Which events" value={d.show} onChange={(e) => onPick(e.target.value)}>
        <optgroup label="Your events">
          <option value="following">Organizations you follow ({d.sets.following})</option>
          {d.feeds.map((f) => (
            <option key={f.id} value={`feed:${f.id}`}>
              {f.name} ({f.count})
            </option>
          ))}
        </optgroup>
        <optgroup label="Browse">
          <option value="all">All events ({d.sets.all})</option>
          {tree.ordered.map(([h, depth]) => (
            <option key={h.id} value={`org:${h.id}`}>
              {"  ".repeat(depth)}
              {h.label} ({tree.total(h.id)})
            </option>
          ))}
        </optgroup>
      </select>
    </>
  );
}

/** What the picked set is: for an organization, a Follow button; for the ones you follow, each with Unfollow. */
function Header({ d, onPick }: { d: EventsPage; onPick: (show: string) => void }) {
  const tree = useTree(d.hosts);
  const byId = new Map(d.hosts.map((h) => [h.id, h]));
  if (d.show === "following") {
    return (
      <div className="events-head">
        <h2>Organizations you follow</h2>
        {d.follows.length ? (
          <>
            <p className="muted">Their events, and those of the organizations under them.</p>
            <div className="follow-list">
              {d.follows.map((id) => {
                const h = byId.get(id);
                return h ? (
                  <span key={id} className="follow-chip">
                    <button className="link-btn" onClick={() => onPick(`org:${id}`)}>
                      {h.label}
                    </button>
                    <FollowButton h={h} compact />
                  </span>
                ) : null;
              })}
            </div>
          </>
        ) : (
          <p className="muted">You don’t follow any organizations yet. Pick one under Browse and choose Follow to see its events here.</p>
        )}
      </div>
    );
  }
  if (d.show.startsWith("feed:")) {
    const f = d.feeds.find((x) => `feed:${x.id}` === d.show);
    return (
      <div className="events-head">
        <h2>{f?.name}</h2>
        <p className="muted">
          Events matching this feed, or tied to an opportunity in it. <Link to={`/feeds/${f?.id}`}>Open the feed</Link>
        </p>
      </div>
    );
  }
  if (d.show.startsWith("org:")) {
    const h = byId.get(d.show.slice(4));
    if (!h) return null;
    const under = tree.children(h.id).map((c) => c.label);
    const parent = byId.get(h.parent);
    return (
      <div className="events-head">
        <div className="head-line">
          <h2>{h.label}</h2>
          <FollowButton h={h} />
        </div>
        <p className="muted">
          {under.length ? `Includes ${joinList(under)}. ` : ""}
          {parent ? (
            <>
              Part of{" "}
              <button className="link-btn" onClick={() => onPick(`org:${parent.id}`)}>
                {parent.label}
              </button>
              .{" "}
            </>
          ) : null}
          {h.followed ? "You follow it, so its events are under Organizations you follow." : "Follow it to see its events under Organizations you follow."}
        </p>
      </div>
    );
  }
  return (
    <div className="events-head">
      <h2>All events</h2>
      <p className="muted">Every event coming up, whoever holds it.</p>
    </div>
  );
}

function FollowButton({ h, compact = false }: { h: EventHost; compact?: boolean }) {
  const client = useQueryClient();
  const { showToast } = useUI();
  const toggle = async () => {
    await api(`/api/events/follows/${encodeURIComponent(h.id)}`, { method: h.followed ? "DELETE" : "PUT" });
    await client.invalidateQueries({ queryKey: ["events"] });
    showToast(h.followed ? `You no longer follow ${h.label}.` : `You follow ${h.label}: its events are under Organizations you follow.`);
  };
  if (compact)
    return (
      <button className="chip-x" aria-label={`Unfollow ${h.label}`} title={`Unfollow ${h.label}`} onClick={toggle}>
        ×
      </button>
    );
  return (
    <button className="btn" aria-pressed={h.followed} onClick={toggle}>
      {h.followed ? "Following ✓" : "Follow"}
    </button>
  );
}

function Rows({ d, filtered }: { d: EventsPage; filtered: boolean }) {
  if (!d.items.length) {
    const text = filtered
      ? "No events here match."
      : d.show === "following"
        ? d.follows.length
          ? "Nothing coming up from the organizations you follow."
          : ""
        : "Nothing coming up.";
    return text ? <p className="empty">{text}</p> : null;
  }
  const out: ReactNode[] = [];
  let last = "";
  for (const n of d.items) {
    if (n.group !== last) {
      out.push(
        <div key={"g" + n.group} className="day-label">
          {n.group}
        </div>,
      );
      last = n.group ?? "";
    }
    out.push(<EventRow key={n.key} n={n} />);
  }
  if (d.total > d.items.length)
    out.push(
      <p key="more" className="muted">
        Showing the first {d.items.length} of {d.total}.
      </p>,
    );
  return <>{out}</>;
}

/** The organizations as a tree (Navy › ONR), biggest first, with counts that include those under each. */
function useTree(hosts: EventHost[]) {
  const byId = new Map(hosts.map((h) => [h.id, h]));
  const kids = new Map<string, EventHost[]>();
  for (const h of hosts) {
    const p = byId.has(h.parent) ? h.parent : "";
    kids.set(p, [...(kids.get(p) ?? []), h]);
  }
  const total = (id: string): number => (byId.get(id)?.count ?? 0) + (kids.get(id) ?? []).reduce((s, c) => s + total(c.id), 0);
  const children = (id: string) => [...(kids.get(id) ?? [])].sort((a, b) => total(b.id) - total(a.id) || a.label.localeCompare(b.label));
  const chain = (id: string): string[] => (byId.has(id) ? [id, ...chain(byId.get(id)!.parent)] : []);
  const ordered: [EventHost, number][] = [];
  const walk = (id: string, depth: number) =>
    children(id).forEach((h) => {
      ordered.push([h, depth]);
      walk(h.id, depth + 1);
    });
  walk("", 0);
  return { roots: children(""), children, total, chain, ordered };
}

function joinList(xs: string[]) {
  return xs.length <= 2 ? xs.join(" and ") : `${xs.slice(0, -1).join(", ")} and ${xs[xs.length - 1]}`;
}
